import { useState } from "react";
import apiClient from "../../api/axios";
import { ExtractedContract, AllDataResponse, ResolveMastersResponse } from "../../types/contract";

interface UseConfirmSaveParams {
  editableContracts: ExtractedContract[];
  setEditableContracts: React.Dispatch<React.SetStateAction<ExtractedContract[]>>;
  setSelectedContract: React.Dispatch<React.SetStateAction<ExtractedContract | null>>;
  setGlobalContracts: (contracts: ExtractedContract[] | ((prev: ExtractedContract[]) => ExtractedContract[])) => void;
  originalContracts: ExtractedContract[];
  /** handleStartEdit 時にディープコピーされた編集前スナップショット */
  originalSelectedContract: ExtractedContract | null;
  selectedEditableContract: ExtractedContract | null;
  changedContractIds: Set<number>;
  setChangedContractIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  setSnackbar: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; severity?: "success" | "info" | "warning" | "error" }>>;
  exitEditMode: () => void;
}

export const useConfirmSave = ({
  editableContracts,
  setEditableContracts,
  setSelectedContract,
  setGlobalContracts,
  originalContracts,
  originalSelectedContract,
  selectedEditableContract,
  changedContractIds,
  setChangedContractIds,
  setSnackbar,
  exitEditMode,
}: UseConfirmSaveParams) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [contractToAction, setContractToAction] = useState<ExtractedContract | null>(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [trashError, setTrashError] = useState<string | null>(null);

  // --- 編集フロー用ステート ---
  const [showMasterConflictModal, setShowMasterConflictModal] = useState(false);
  const [masterConflictData, setMasterConflictData] = useState<ResolveMastersResponse | null>(null);
  const [pendingContractToSave, setPendingContractToSave] = useState<ExtractedContract | null>(null);

  const fetchTrashData = async () => {
    try {
      setOperationLoading(true);
      setTrashError(null);
      const response = await apiClient.get<AllDataResponse>(
        `/api/trash/`
      );
      const sortedContracts = response.data.contracts.sort((a, b) => {
        // deleted_atからexpiryDateを計算し、昇順でソートする
        // deleted_atが存在しない場合はInfinityとして最後にする
        const aExpiry = a.deleted_at ? new Date(new Date(a.deleted_at).setDate(new Date(a.deleted_at).getDate() + 30)).getTime() : Infinity;
        const bExpiry = b.deleted_at ? new Date(new Date(b.deleted_at).setDate(new Date(b.deleted_at).getDate() + 30)).getTime() : Infinity;

        return aExpiry - bExpiry;
      });
      // For trash view, we manage the list locally
      setEditableContracts(sortedContracts);
      setSelectedContract(sortedContracts[0] || null);
    } catch (err) {
      setTrashError("ごみ箱データの取得中にエラーが発生しました。");
      console.error(err);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteDetails = async (item: ExtractedContract) => {
    setContractToAction(item);
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (!contractToAction || contractToAction.id === undefined) return;

    setOperationLoading(true); // スピナーを表示
    try {
      // The ID from the contract/room is used to call the new endpoint
      await apiClient.delete(`/api/rooms/${contractToAction.id}`);
      setSnackbar({
        open: true,
        message: "データをごみ箱に移動しました。",
        severity: "success",
      });

      // Update global and local state instead of re-fetching.
      // Filter out the single deleted contract by its ID.
      const newContracts = editableContracts.filter(c => c.id !== contractToAction.id);
      setGlobalContracts(prev => prev.filter(c => c.id !== contractToAction.id));
      setEditableContracts(newContracts);

    } catch (err) {
      setTrashError("データの削除中にエラーが発生しました。"); // エラー時もエラー状態をセット
      setSnackbar({
        open: true,
        message: "データの削除中にエラーが発生しました。",
        severity: "error",
      });
      console.error(err);
    } finally {
      setOperationLoading(false); // 処理終了時にスピナーを非表示
    }
  };

  const handleRestore = async (item: ExtractedContract) => {
    if (!item || item.id === undefined) return;
    setContractToAction(item);
    setShowRestoreConfirm(true);
  };

  const executeRestore = async () => {
    if (!contractToAction || contractToAction.id === undefined) return;

    setOperationLoading(true);
    try {
      await apiClient.post(`/api/rooms/${contractToAction.id}/restore`);
      setSnackbar({
        open: true,
        message: "データが正常に復元されました。",
        severity: "success",
      });

      // 1. Remove the item from the local trash list
      setEditableContracts(prev => prev.filter(c => c.id !== contractToAction.id));

      // 2. Add the restored item back to the global cache
      // Make sure to remove the deletion timestamp
      const restoredContract = { ...contractToAction, deleted_at: undefined };
      setGlobalContracts(prev => [...prev, restoredContract].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));

      setContractToAction(null); // Clear the action item

    } catch (err) {
      setTrashError("データの復元中にエラーが発生しました。");
      console.error(err);
    } finally {
      setOperationLoading(false);
    }
  };

  // ============================================================
  // 保存ロジック（内部ヘルパー）
  // ============================================================

  /** PATCH /api/contracts/ を呼んでグローバルステートを更新する（通常 UPDATE / 一括適用） */
  const _executePatch = async (contractsToSave: ExtractedContract[]) => {
    setOperationLoading(true);
    try {
      const response = await apiClient.patch(`/api/contracts/`, { contracts: contractsToSave });
      setSnackbar({ open: true, message: "変更が正常に保存されました。", severity: "success" });

      const updatedChangedContractIds = new Set(changedContractIds);
      contractsToSave.forEach(c => updatedChangedContractIds.delete(c.id!));
      setChangedContractIds(updatedChangedContractIds);

      // サーバーから返された正規化後のデータでキャッシュを更新
      const updatedContractsFromServer = response.data.updated_contracts || [];
      const newGlobalContracts = originalContracts.map(originalContract => {
        const updatedVersion = updatedContractsFromServer.find((updated: any) => updated.id === originalContract.id);
        return updatedVersion ? updatedVersion : originalContract;
      });
      setGlobalContracts(newGlobalContracts);
      setEditableContracts(newGlobalContracts);

      // 選択中の契約をサーバーの最新データで更新
      const updatedSelected = updatedContractsFromServer.find(
        (updated: any) => updated.id === selectedEditableContract?.id
      );
      if (updatedSelected) {
        setSelectedContract(updatedSelected);
      }

      exitEditMode();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "保存中にエラーが発生しました。", severity: "error" });
    } finally {
      setOperationLoading(false);
    }
  };

  /** PATCH /api/contracts/ を再挿入モードで呼ぶ（この部屋のみ変更） */
  const _executeReinsertion = async (contractToSave: ExtractedContract) => {
    if (!selectedEditableContract?.id) return;
    setOperationLoading(true);
    try {
      const response = await apiClient.patch(`/api/contracts/`, {
        contracts: [contractToSave],
        reinsertion_mode: true,
        old_room_id: selectedEditableContract.id,
      });
      setSnackbar({ open: true, message: "変更が正常に保存されました。", severity: "success" });

      // 変更済みIDセットから旧IDを除去
      const updatedChangedContractIds = new Set(changedContractIds);
      updatedChangedContractIds.delete(selectedEditableContract.id!);
      setChangedContractIds(updatedChangedContractIds);

      // 旧部屋を除去し新しい契約データを追加
      const updatedContractsFromServer: ExtractedContract[] = response.data.updated_contracts || [];
      const oldRoomId: number = Number(response.data.old_room_id);
      // updated_contracts には同じ家主/建物に紐づく全部屋が含まれる場合があるため、
      // oldRoomId だけでなく更新済み全IDも originalContracts から除外して重複を防ぐ
      const updatedIds = new Set(updatedContractsFromServer.map(c => c.id));
      const newGlobalContracts = [
        ...originalContracts.filter(c => c.id !== oldRoomId && !updatedIds.has(c.id)),
        ...updatedContractsFromServer,
      ].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      setGlobalContracts(newGlobalContracts);
      setEditableContracts(newGlobalContracts);

      // 新しい契約を選択状態にする
      if (updatedContractsFromServer.length > 0) {
        setSelectedContract(updatedContractsFromServer[0]);
      }

      exitEditMode();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "保存中にエラーが発生しました。", severity: "error" });
    } finally {
      setOperationLoading(false);
      setPendingContractToSave(null);
    }
  };

  // ============================================================
  // 保存エントリポイント
  // ============================================================

  const handleSaveSingleContractChanges = async () => {
    if (!selectedEditableContract || selectedEditableContract.id === undefined) return;

    const contractsToSave = editableContracts.filter(c => c.id === selectedEditableContract.id);
    if (contractsToSave.length === 0) return;
    const editedContract = contractsToSave[0];

    // 家主・建物マスタの変更有無を判定
    // originalSelectedContract は handleStartEdit 時にディープコピーされた確実な編集前スナップショット。
    // originalContracts は AllDataContext 経由で参照が共有されることがあるため使わない。
    if (!originalSelectedContract) {
      await _executePatch(contractsToSave);
      return;
    }

    const sv = (key: keyof ExtractedContract, c: ExtractedContract): string | null =>
      (c[key] as { value?: string | null } | null | undefined)?.value ?? null;

    const ownerMasterChanged =
      sv("owner_name", originalSelectedContract)              !== sv("owner_name", editedContract) ||
      sv("owner_name_kana", originalSelectedContract)         !== sv("owner_name_kana", editedContract) ||
      sv("owner_postal_code", originalSelectedContract)       !== sv("owner_postal_code", editedContract) ||
      sv("owner_address1", originalSelectedContract)          !== sv("owner_address1", editedContract) ||
      sv("owner_address2", originalSelectedContract)          !== sv("owner_address2", editedContract) ||
      sv("owner_home_phone", originalSelectedContract)        !== sv("owner_home_phone", editedContract) ||
      sv("owner_mobile_phone", originalSelectedContract)      !== sv("owner_mobile_phone", editedContract) ||
      sv("owner_bank_1_branch_code", originalSelectedContract)         !== sv("owner_bank_1_branch_code", editedContract) ||
      sv("owner_bank_1_account_type", originalSelectedContract)        !== sv("owner_bank_1_account_type", editedContract) ||
      sv("owner_bank_1_account_number", originalSelectedContract)      !== sv("owner_bank_1_account_number", editedContract) ||
      sv("owner_bank_1_account_holder_name", originalSelectedContract) !== sv("owner_bank_1_account_holder_name", editedContract) ||
      sv("owner_bank_2_branch_code", originalSelectedContract)         !== sv("owner_bank_2_branch_code", editedContract) ||
      sv("owner_bank_2_account_type", originalSelectedContract)        !== sv("owner_bank_2_account_type", editedContract) ||
      sv("owner_bank_2_account_number", originalSelectedContract)      !== sv("owner_bank_2_account_number", editedContract) ||
      sv("owner_bank_2_account_holder_name", originalSelectedContract) !== sv("owner_bank_2_account_holder_name", editedContract);

    const buildingMasterChanged =
      originalSelectedContract.property_name                  !== editedContract.property_name ||
      sv("building_code", originalSelectedContract)           !== sv("building_code", editedContract) ||
      sv("building_name_kana", originalSelectedContract)      !== sv("building_name_kana", editedContract) ||
      sv("property_postal_code", originalSelectedContract)    !== sv("property_postal_code", editedContract) ||
      sv("property_address", originalSelectedContract)        !== sv("property_address", editedContract) ||
      sv("property_address2", originalSelectedContract)       !== sv("property_address2", editedContract);

    // 名前フィールドのみ変化チェック（照合が必要かどうかの判定用）
    const ownerNameChanged =
      sv("owner_name", originalSelectedContract) !== sv("owner_name", editedContract);
    const buildingNameChanged =
      originalSelectedContract.property_name !== editedContract.property_name;

    if (ownerMasterChanged || buildingMasterChanged) {
      if (!ownerNameChanged && !buildingNameChanged) {
        // 名前は変わっていない（電話・住所・銀行情報などの編集）
        // → 同一マスタを編集内容で更新するだけ。照合・モーダル不要。
        await _executeReinsertion(editedContract);
        return;
      }

      // 名前が変わった場合のみ照合してモーダル表示
      setPendingContractToSave(editedContract);
      try {
        const response = await apiClient.post<ResolveMastersResponse>(`/api/contracts/resolve-masters`, {
          room_id: editedContract.id,
          owner_name: editedContract.owner_name?.value ?? "",
          property_name: editedContract.property_name ?? "",
        });
        const { owner_match } = response.data;
        if (owner_match) {
          // 既存マスタが見つかった: 採用するか選択させる
          setMasterConflictData(response.data);
          setShowMasterConflictModal(true);
        } else {
          // 一致なし: 新規マスタを作成して再挿入
          await _executeReinsertion(editedContract);
        }
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: "マスタ照合中にエラーが発生しました。", severity: "error" });
        setPendingContractToSave(null);
      }
      return;
    }

    // マスタ変更なし: 直接 UPDATE
    await _executePatch(contractsToSave);
  };

  // ============================================================
  // 既存マスタへの紐づけ確認モーダルのハンドラー
  // ============================================================

  /**
   * 既存マスタへの紐づけを確定する。
   * 常に既存データを採用（keep）する。
   * 既存マスタを書き換えたい場合はそのマスタに紐づく部屋から直接編集する。
   */
  const handleMasterConflictConfirm = async () => {
    setShowMasterConflictModal(false);
    if (!pendingContractToSave || !masterConflictData) return;

    let contractToSave = { ...pendingContractToSave };

    // 既存マスタの全フィールドでフォームを上書きして紐づける
    if (masterConflictData.owner_match) {
      const o = masterConflictData.owner_match;
      contractToSave = {
        ...contractToSave,
        owner_name:                       { value: o.name,                          source_document: "既存データ" },
        owner_name_kana:                  { value: o.name_kana,                     source_document: "既存データ" },
        owner_postal_code:                { value: o.postal_code,                   source_document: "既存データ" },
        owner_address1:                   { value: o.address1,                      source_document: "既存データ" },
        owner_address2:                   { value: o.address2,                      source_document: "既存データ" },
        owner_home_phone:                 { value: o.home_phone,                    source_document: "既存データ" },
        owner_mobile_phone:               { value: o.mobile_phone,                  source_document: "既存データ" },
        owner_bank_1_branch_code:         { value: o.bank_1_branch_code,            source_document: "既存データ" },
        owner_bank_1_account_type:        { value: o.bank_1_account_type,           source_document: "既存データ" },
        owner_bank_1_account_number:      { value: o.bank_1_account_number,         source_document: "既存データ" },
        owner_bank_1_account_holder_name: { value: o.bank_1_account_holder_name,    source_document: "既存データ" },
        owner_bank_2_branch_code:         { value: o.bank_2_branch_code,            source_document: "既存データ" },
        owner_bank_2_account_type:        { value: o.bank_2_account_type,           source_document: "既存データ" },
        owner_bank_2_account_number:      { value: o.bank_2_account_number,         source_document: "既存データ" },
        owner_bank_2_account_holder_name: { value: o.bank_2_account_holder_name,    source_document: "既存データ" },
      };
    }

    if (masterConflictData.building_match) {
      const b = masterConflictData.building_match;
      contractToSave = {
        ...contractToSave,
        property_name:        b.name ?? contractToSave.property_name,
        building_code:        { value: b.building_code, source_document: "既存データ" },
        building_name_kana:   { value: b.name_kana,     source_document: "既存データ" },
        property_postal_code: { value: b.postal_code,   source_document: "既存データ" },
        property_address:     { value: b.address1,      source_document: "既存データ" },
        property_address2:    { value: b.address2,      source_document: "既存データ" },
      };
    }

    setMasterConflictData(null);
    await _executeReinsertion(contractToSave);
  };

  return {
    showDeleteConfirm,
    setShowDeleteConfirm,
    showRestoreConfirm,
    setShowRestoreConfirm,
    contractToAction,
    operationLoading,
    trashError,
    setTrashError,
    handleDeleteDetails,
    executeDelete,
    handleRestore,
    executeRestore,
    handleSaveSingleContractChanges,
    fetchTrashData,
    // 編集フロー
    showMasterConflictModal,
    setShowMasterConflictModal,
    masterConflictData,
    setPendingContractToSave,
    handleMasterConflictConfirm,
  };
};
